import { LitElement, html, render } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property, query, state } from 'lit/decorators.js';

// Import styles from separate files
import globalStyles from './nuvance-decoupled-search-input.global.pcss?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import { debounce } from '../../../utilities/debounce';
import { NuvanceDecoupledSearch } from '../nuvance-decoupled-search/nuvance-decoupled-search';
import { NuvanceDecoupledSearchTypeaheadResults } from '../nuvance-decoupled-search-typeahead-results/nuvance-decoupled-search-typeahead-results';

/**
 * Interface for typeahead result items
 */
interface TypeaheadResult {
  /** The URL to navigate to when the result is clicked */
  url?: string;
  /** The display label for the result, may contain HTML */
  label?: string;
  /** Alternative display text if label is not available */
  title?: string;
  /** Optional additional data that might be returned by the API */
  [key: string]: unknown;
}

/**
 * Interface for parsed endpoint configuration
 */
interface ParsedEndpoint {
  baseUrl: string;
  queryParam: string;
}

/**
 * @element nuvance-decoupled-search-input
 *
 * A search input component that works with nuvance-decoupled-search components.
 * This component renders an input element and submit button in the light DOM.
 * On submit, it updates the query attribute of any nuvance-decoupled-search elements
 * within the specified context.
 *
 * @attr {string} search-submit-context - CSS selector for the context in which to find nuvance-decoupled-search elements
 * @attr {string} placeholder - Placeholder text for the search input
 * @attr {string} search-param - The URL parameter name for the search query (should match the search-param of nuvance-decoupled-search)
 * @attr {string} search-endpoint - The endpoint for general search results (format: "base-url?params;query-param")
 * @attr {string} locations-endpoint - The endpoint for location results (format: "base-url?params;query-param")
 * @attr {string} doctors-endpoint - The endpoint for doctor results (format: "base-url?params;query-param")
 * @attr {string} services-endpoint - The endpoint for service results (format: "base-url?params;query-param")
 * @attr {string} news-endpoint - The endpoint for news results (format: "base-url?params;query-param")
 * @attr {string} search-label - Label for the search results section
 * @attr {string} locations-label - Label for the locations section
 * @attr {string} doctors-label - Label for the doctors section
 * @attr {string} services-label - Label for the services section
 * @attr {string} news-label - Label for the news section
 * @attr {number} max-history-items - Maximum number of search queries to store in history
 */
@customElement('nuvance-decoupled-search-input')
export class NuvanceDecoupledSearchInput extends LitElement {
  /**
   * AdoptedStyleSheets controller for managing component styles
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
  });

  /**
   * CSS selector for the context in which to find nuvance-decoupled-search elements
   */
  @property({ type: String, reflect: true, attribute: 'search-submit-context' })
  searchSubmitContext = 'body';

  /**
   * Placeholder text for the search input
   */
  @property({ type: String, reflect: true })
  placeholder = 'Search...';

  /**
   * The URL parameter name for the search query
   * This should match the search-param attribute of the nuvance-decoupled-search component
   */
  @property({ type: String, reflect: true, attribute: 'search-param' })
  searchParam = 'search_api_fulltext';

  /**
   * The endpoint for general search results
   * Format: "base-url?params;query-param"
   * Example: "/search_api_autocomplete/search?display=0_main&filter=search_api_fulltext;q"
   */
  @property({ type: String, reflect: true, attribute: 'search-endpoint' })
  searchEndpoint =
    '/search_api_autocomplete/search?display=0_main&filter=search_api_fulltext;q';

  /**
   * The endpoint for location results
   * Format: "base-url?params;query-param"
   */
  @property({ type: String, reflect: true, attribute: 'locations-endpoint' })
  locationsEndpoint =
    '/search_api_autocomplete/search?display=1_locations&filter=search_api_fulltext;q';

  /**
   * The endpoint for doctor results
   * Format: "base-url?params;query-param"
   */
  @property({ type: String, reflect: true, attribute: 'doctors-endpoint' })
  doctorsEndpoint = '/api/kyruus/provider-typeahead;q';

  /**
   * The endpoint for service results
   * Format: "base-url?params;query-param"
   */
  @property({ type: String, reflect: true, attribute: 'services-endpoint' })
  servicesEndpoint =
    '/search_api_autocomplete/search?display=3_services_treatments&filter=search_api_fulltext;q';

  /**
   * The endpoint for news results
   * Format: "base-url?params;query-param"
   */
  @property({ type: String, reflect: true, attribute: 'news-endpoint' })
  newsEndpoint =
    '/search_api_autocomplete/search?display=4_news&filter=search_api_fulltext;q';

  /**
   * Minimum number of characters required before triggering typeahead fetch
   */
  @property({ type: Number, reflect: true, attribute: 'min-chars' })
  minChars = 2;

  /**
   * Maximum number of search queries to store in history
   */
  @property({ type: Number, reflect: true, attribute: 'max-history-items' })
  maxHistoryItems = 10;

  /**
   * Local storage key for search history
   */
  @property({ type: String, reflect: true, attribute: 'history-storage-key' })
  historyStorageKey = 'nuvance-decoupled-search-history';

  /**
   * Label for the search results section
   */
  @property({ type: String, reflect: true, attribute: 'search-label' })
  searchLabel = 'Search Results';

  /**
   * Label for the locations section
   */
  @property({ type: String, reflect: true, attribute: 'locations-label' })
  locationsLabel = 'Locations';

  /**
   * Label for the doctors section
   */
  @property({ type: String, reflect: true, attribute: 'doctors-label' })
  doctorsLabel = 'Doctors';

  /**
   * Label for the services section
   */
  @property({ type: String, reflect: true, attribute: 'services-label' })
  servicesLabel = 'Services';

  /**
   * Label for the news section
   */
  @property({ type: String, reflect: true, attribute: 'news-label' })
  newsLabel = 'News';

  /**
   * Reference to the search input element
   */
  @query('#search-input')
  private searchInput!: HTMLInputElement;

  /**
   * Reference to the typeahead results component
   */
  @query('nuvance-decoupled-search-typeahead-results')
  private typeaheadResults!: NuvanceDecoupledSearchTypeaheadResults;

  /**
   * Debug mode flag
   */
  @property({ type: Boolean, attribute: 'debug' })
  debug = false;

  /**
   * Debounced function for fetching typeahead results
   */
  private debouncedFetchTypeaheadResults = debounce(
    this.fetchTypeaheadResults.bind(this),
    300,
    false,
    this.debug
  );

  /**
   * Flag to track if the form has been submitted
   * This prevents typeahead results from showing after form submission
   */
  @state()
  private isFormSubmitted = false;

  /**
   * Set the component's render method to create its content in the light DOM
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Logs debug messages if debug mode is enabled
   */
  private debugLog(message: string, ...args: unknown[]): void {
    if (this.debug) {
      console.log(`[NuvanceDecoupledSearchInput] ${message}`, ...args);
    }
  }

  /**
   * Gets the current search history from localStorage
   */
  private getSearchHistory(): string[] {
    try {
      const historyJson = localStorage.getItem(this.historyStorageKey);
      if (historyJson) {
        const history = JSON.parse(historyJson);
        if (Array.isArray(history)) {
          this.debugLog('Retrieved search history from localStorage', {
            history,
          });
          return history;
        }
      }
    } catch (error) {
      this.debugLog('Error retrieving search history from localStorage', {
        error,
      });
    }

    return [];
  }

  /**
   * Saves the search history to localStorage
   */
  private saveSearchHistory(history: string[]): void {
    try {
      localStorage.setItem(this.historyStorageKey, JSON.stringify(history));
      this.debugLog('Saved search history to localStorage', { history });
    } catch (error) {
      this.debugLog('Error saving search history to localStorage', { error });
    }
  }

  /**
   * Adds a search query to the history
   */
  private addToSearchHistory(query: string): void {
    if (!query.trim()) {
      this.debugLog('Query is empty, not adding to history');
      return;
    }

    const history = this.getSearchHistory();

    // Remove the query if it already exists (to avoid duplicates)
    const filteredHistory = history.filter(item => item !== query);

    // Add the new query to the beginning of the array
    filteredHistory.unshift(query);

    // Limit the history to the maximum number of items
    const limitedHistory = filteredHistory.slice(0, this.maxHistoryItems);

    this.saveSearchHistory(limitedHistory);
    this.debugLog('Added query to search history', {
      query,
      history: limitedHistory,
    });

    // Dispatch a custom event to notify other components that the search history has been updated
    this.dispatchEvent(
      new CustomEvent('search-history-updated', {
        bubbles: true,
        composed: true,
        detail: { history: limitedHistory },
      })
    );
  }

  /**
   * Lifecycle callback when the component is connected to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.debugLog('Component connected to DOM');
  }

  /**
   * Lifecycle callback when the component is first updated
   * This is called after the component's first render
   */
  firstUpdated(): void {
    this.debugLog('Component first updated');

    // Initialize the input value directly from the URL
    this.initializeFromUrl();
  }

  /**
   * Initializes the input value directly from the URL search parameter
   * This handles both direct search parameters and those embedded in content path parameters
   */
  private initializeFromUrl(): void {
    if (!this.searchInput) {
      this.debugLog('Search input element not found, cannot initialize');
      return;
    }

    // Get the current URL
    const url = new URL(window.location.href);

    // First try to get the search query directly from the URL parameter
    let searchQuery = url.searchParams.get(this.searchParam);

    this.debugLog('Checking for direct search parameter', {
      searchParam: this.searchParam,
      searchQuery,
    });

    // If not found directly, look for it in the content path parameters
    if (!searchQuery) {
      // Look through all URL parameters for content_path_* parameters
      for (const [key, value] of url.searchParams.entries()) {
        if (key.startsWith('content_path_')) {
          this.debugLog('Checking content path parameter', { key, value });

          // Try to extract the search parameter from the content path
          try {
            // The content path value might be something like "/api/site-search/news?search_api_fulltext=et"
            // We need to extract the search_api_fulltext parameter from this
            if (value.includes('?')) {
              const contentPathQueryString = value.split('?')[1];
              const contentPathParams = new URLSearchParams(
                contentPathQueryString
              );
              const contentPathSearchQuery = contentPathParams.get(
                this.searchParam
              );

              if (contentPathSearchQuery) {
                searchQuery = contentPathSearchQuery;
                this.debugLog('Found search query in content path', {
                  contentPath: value,
                  searchParam: this.searchParam,
                  searchQuery,
                });
                break; // Found what we need, exit the loop
              }
            }
          } catch (error) {
            this.debugLog('Error parsing content path parameter', {
              key,
              value,
              error,
            });
          }
        }
      }
    }

    this.debugLog('Initializing from URL', {
      url: url.toString(),
      searchParam: this.searchParam,
      searchQuery,
    });

    // If we have a search query, set the input value
    if (searchQuery) {
      this.searchInput.value = searchQuery;
      this.debugLog('Set input value from URL', {
        value: searchQuery,
      });
    } else {
      this.debugLog('No search query found in URL or content paths');
    }
  }

  /**
   * Handles the form submission
   */
  private handleSubmit(event: Event): void {
    event.preventDefault();

    // Set flag to prevent showing typeahead results
    this.isFormSubmitted = true;

    // Hide any currently visible typeahead results
    if (this.typeaheadResults) {
      this.typeaheadResults.hide();
    }

    const query = this.searchInput?.value.trim() || '';
    this.debugLog('Search submitted', { query });

    // Add the query to the search history
    this.addToSearchHistory(query);

    // Find all nuvance-decoupled-search elements within the context
    const contextElement =
      document.querySelector(this.searchSubmitContext) || document;
    const searchElements = contextElement.querySelectorAll(
      'nuvance-decoupled-search'
    );

    this.debugLog('Found search elements', {
      count: searchElements.length,
      context: this.searchSubmitContext,
    });

    // If no search elements found, construct URL and redirect
    if (searchElements.length === 0) {
      const encodedQuery = encodeURIComponent(query);
      const searchUrl = `/search-results?tab-state-search-tabs-id-main-site-search=tab-search-results&content_path_doctors=/api/kyruus/search?keyword=${encodedQuery}&content_path_locations=/api/site-search/locations?search_api_fulltext=${encodedQuery}&content_path_services_treatments=/api/site-search/services-treatments?search_api_fulltext=${encodedQuery}&content_path_search_results=/api/site-search/main?search_api_fulltext=${encodedQuery}&content_path_news=/api/site-search/news?search_api_fulltext=${encodedQuery}`;

      this.debugLog('No search elements found, redirecting to search page', {
        searchUrl,
      });

      window.location.href = searchUrl;
      return;
    }

    // Update the query attribute for each search element
    searchElements.forEach(element => {
      // Cast the element to NuvanceDecoupledSearch
      const searchElement = element as NuvanceDecoupledSearch;

      // Ensure the isUpdatingQuery property is set to false
      // This ensures that the query change will trigger a fetch
      searchElement.isUpdatingQuery = false;
      this.debugLog('Set isUpdatingQuery property to false', {
        element: searchElement,
      });

      // Set the query attribute
      searchElement.query = query;

      this.debugLog('Updated query attribute', {
        element: searchElement,
        query,
        isUpdatingQuery: searchElement.isUpdatingQuery,
      });
    });

    // Close global search modal if it is open
    const globalSearchModalCloseButton = document.querySelector(
      'nuvance-top-bar .global-search outline-modal [slot="close-button"] button'
    );
    if (globalSearchModalCloseButton) {
      this.debugLog('Closing global search modal', {
        element: globalSearchModalCloseButton,
      });

      (globalSearchModalCloseButton as HTMLElement).click();
    }
  }

  /**
   * Handles input changes for typeahead functionality
   */
  private handleInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const query = input.value.trim();

    // Reset form submission flag when user starts typing
    this.isFormSubmitted = false;

    // Only fetch results if we have enough characters
    if (query.length >= this.minChars) {
      this.debugLog('Input has enough characters, fetching results', {
        query,
        minChars: this.minChars,
        queryLength: query.length,
      });
      this.debouncedFetchTypeaheadResults(query);
    } else {
      // Hide typeahead results if query is too short
      if (this.typeaheadResults) {
        this.typeaheadResults.hide();
      }
      this.debugLog('Input too short for typeahead', {
        query,
        minChars: this.minChars,
        queryLength: query.length,
      });
    }
  }

  /**
   * Parses an endpoint string into its base URL and query parameter
   */
  private parseEndpoint(endpoint: string): ParsedEndpoint | null {
    if (!endpoint) {
      return null;
    }

    // Split on semicolon to separate base URL with params from query param
    const [baseUrlWithParams, queryParam] = endpoint.split(';');

    if (!queryParam) {
      this.debugLog('Invalid endpoint format - missing query parameter', {
        endpoint,
      });
      return null;
    }

    return {
      baseUrl: baseUrlWithParams,
      queryParam: queryParam.trim(),
    };
  }

  /**
   * Fetches typeahead results from all configured endpoints
   */
  private async fetchTypeaheadResults(query: string): Promise<void> {
    // If form was submitted, don't show typeahead results
    if (this.isFormSubmitted) {
      this.debugLog('Form was submitted, not showing typeahead results', {
        query,
      });
      return;
    }

    this.debugLog('Fetching typeahead results', { query });

    // Create an array of fetch promises for each endpoint
    const fetchPromises: Promise<void>[] = [];
    let hasResults = false;

    // Helper function to fetch and update results for an endpoint
    const fetchEndpoint = async (
      endpoint: string,
      slotName: string,
      label: string
    ) => {
      // Check again if form was submitted before processing results
      if (this.isFormSubmitted) {
        this.debugLog(
          'Form was submitted during fetch, not processing results',
          { endpoint }
        );
        return;
      }

      const parsedEndpoint = this.parseEndpoint(endpoint);
      if (!parsedEndpoint) {
        this.debugLog('Invalid endpoint configuration, skipping', {
          endpoint,
          slotName,
        });
        return;
      }

      try {
        const url = new URL(parsedEndpoint.baseUrl, window.location.origin);
        url.searchParams.set(parsedEndpoint.queryParam, query);

        this.debugLog('Fetching from endpoint', {
          url: url.toString(),
          queryParam: parsedEndpoint.queryParam,
          fullUrl: url.toString(),
        });

        const response = await fetch(url.toString());
        this.debugLog('Response received', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
        });

        if (!response.ok) {
          throw new Error(
            `HTTP error! status: ${response.status} - ${response.statusText}`
          );
        }

        const data = await response.json();
        this.debugLog('Received data from endpoint', {
          endpoint: parsedEndpoint.baseUrl,
          slotName,
          data,
          dataType: typeof data,
          isArray: Array.isArray(data),
          dataLength: Array.isArray(data) ? data.length : 'not an array',
          firstItem: Array.isArray(data) && data.length > 0 ? data[0] : null,
        });

        // Update the slot content
        if (Array.isArray(data) && data.length > 0) {
          this.updateSlotContent(slotName, data, label);
          hasResults = true;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        this.debugLog('Error fetching from endpoint', {
          endpoint: parsedEndpoint.baseUrl,
          slotName,
          error,
          errorMessage,
          query,
        });
        // Clear any existing content for this slot on error
        this.clearSlotAttribute(slotName);
      }
    };

    // Add fetch promises for each endpoint
    if (this.searchEndpoint) {
      fetchPromises.push(
        fetchEndpoint(this.searchEndpoint, 'search-results', this.searchLabel)
      );
    }

    if (this.locationsEndpoint) {
      fetchPromises.push(
        fetchEndpoint(this.locationsEndpoint, 'locations', this.locationsLabel)
      );
    }

    if (this.doctorsEndpoint) {
      fetchPromises.push(
        fetchEndpoint(this.doctorsEndpoint, 'doctors', this.doctorsLabel)
      );
    }

    if (this.servicesEndpoint) {
      fetchPromises.push(
        fetchEndpoint(this.servicesEndpoint, 'services', this.servicesLabel)
      );
    }

    if (this.newsEndpoint) {
      fetchPromises.push(
        fetchEndpoint(this.newsEndpoint, 'news', this.newsLabel)
      );
    }

    // Wait for all fetches to complete
    await Promise.all(fetchPromises);

    // Only show the typeahead results if we have actual content to display
    // and the form hasn't been submitted
    if (this.typeaheadResults) {
      if (hasResults && !this.isFormSubmitted) {
        this.typeaheadResults.show();
        this.debugLog('Showing typeahead results - content available');
      } else {
        this.typeaheadResults.hide();
        this.debugLog(
          'Hiding typeahead results - no content available or form submitted'
        );
      }
    }
  }

  /**
   * Clears the boolean attribute for a slot
   */
  private clearSlotAttribute(slotName: string): void {
    if (!this.typeaheadResults) return;

    const hasAttribute = `has-${slotName.replace(/_/g, '-')}`;
    this.typeaheadResults.removeAttribute(hasAttribute);
    this.debugLog('Cleared boolean attribute', { hasAttribute });
  }

  /**
   * Updates the content of a slot with the fetched results
   * This method handles:
   * 1. Removing any existing content in the slot
   * 2. Creating new content using Lit's template system
   * 3. Setting appropriate boolean attributes for styling
   *
   * @param slotName - The name of the slot to update
   * @param data - Array of typeahead results to display
   * @param label - The section label to display above the results
   */
  private updateSlotContent(
    slotName: string,
    data: TypeaheadResult[],
    label: string
  ): void {
    if (!this.typeaheadResults) {
      this.debugLog('Typeahead results component not found', { slotName });
      return;
    }

    this.debugLog('Updating slot content', {
      slotName,
      label,
      dataLength: Array.isArray(data) ? data.length : 'not an array',
      dataType: typeof data,
      data,
    });

    // Remove any existing content in this slot
    const existingContent = this.typeaheadResults.querySelector(
      `[slot="${slotName}"]`
    );
    if (existingContent) {
      this.debugLog('Removing existing content', { slotName });
      existingContent.remove();
      this.clearSlotAttribute(slotName);
    }

    // Only proceed if we have valid data to display
    if (!Array.isArray(data) || data.length === 0) {
      this.debugLog('No valid data to display', { slotName });
      return;
    }

    // Create the content using Lit's template system
    const content = html`
      <h3>${label}</h3>
      <ul>
        ${data.map(
          item => html`
            <li>
              <a href="${item.url || '#'}">
                ${this.renderHtmlContent(
                  item.label || item.title || 'Untitled'
                )}
              </a>
            </li>
          `
        )}
      </ul>
    `;

    // Add the new content using Lit's render method
    const container = document.createElement('div');
    container.setAttribute('slot', slotName);
    this.typeaheadResults.appendChild(container);
    render(content, container);

    // Set the appropriate boolean attribute based on the slot name
    // This is used for styling and visibility control
    const hasAttribute = `has-${slotName.replace(/_/g, '-')}`;
    this.typeaheadResults.setAttribute(hasAttribute, '');
    this.debugLog('Set boolean attribute', { hasAttribute });
  }

  /**
   * Safely renders HTML content from the API response
   * This method extracts the actual content from the Drupal template output
   * and renders it safely using Lit's unsafeHTML directive
   */
  private renderHtmlContent(content: string): unknown {
    if (!content) {
      return 'Untitled';
    }

    // Check if the content is already in the expected format
    if (content.includes('autocomplete-suggestion-label')) {
      const match = content.match(
        /<span class="autocomplete-suggestion-label">(.*?)<\/span>/
      );
      if (match) {
        return html`${unsafeHTML(match[1])}`;
      }
    }

    // Fallback to rendering the raw content if it doesn't match the expected format
    return html`${unsafeHTML(content)}`;
  }

  /**
   * Renders the component
   */
  render() {
    return html`
      <form class="nuvance-search-form" @submit="${this.handleSubmit}">
        <div class="search-input-container">
          <input
            id="search-input"
            type="text"
            placeholder="${this.placeholder}"
            class="search-input"
            @input="${this.handleInput}"
          />
          <button type="submit" class="search-submit">
            <span class="visually-hidden">Search</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="6"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </button>
        </div>
      </form>
      <nuvance-decoupled-search-typeahead-results
        ?debug="${this.debug}"
      ></nuvance-decoupled-search-typeahead-results>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nuvance-decoupled-search-input': NuvanceDecoupledSearchInput;
  }
}
